/** The 10 sensor types the `/sensors` API defines (confirmed with BE 2026-09-22, HEADLIGHTS/SOS included). */
export type SensorType =
  | "WEIGHT"
  | "TEMPERATURE"
  | "DOOR_STATUS"
  | "IBUTTON"
  | "PTO"
  | "SEAT_BELT"
  | "REFRIGERATOR_STATE"
  | "HEADLIGHTS"
  | "SOS"
  | "RFID";

export const SENSOR_TYPES: readonly SensorType[] = [
  "WEIGHT",
  "TEMPERATURE",
  "DOOR_STATUS",
  "IBUTTON",
  "PTO",
  "SEAT_BELT",
  "REFRIGERATOR_STATE",
  "HEADLIGHTS",
  "SOS",
  "RFID",
];

/**
 * IBUTTON -- the one sensor type this app still *assumes* has the plain,
 * IO-numbers-only config shape (see SimpleIoConfig), carried over from the
 * legacy admin screens. Unverified against the real `GET /sensors/form`
 * response; DOOR_STATUS, SEAT_BELT, REFRIGERATOR_STATE, HEADLIGHTS and SOS
 * all used to sit in this same assumed bucket alongside PTO, but each was
 * confirmed (2026-09-23) to actually match PTO's richer value-mapped shape
 * instead -- see {@link ValueMapSensorType}. Don't assume IBUTTON matches
 * either until it's checked too.
 */
export type SimpleIoSensorType = "IBUTTON";

export const SIMPLE_IO_SENSOR_TYPES: readonly SimpleIoSensorType[] = ["IBUTTON"];

export function isSimpleIoSensorType(
  sensorType: SensorType,
): sensorType is SimpleIoSensorType {
  return (SIMPLE_IO_SENSOR_TYPES as readonly SensorType[]).includes(sensorType);
}

/**
 * DOOR_STATUS, PTO, SEAT_BELT, REFRIGERATOR_STATE, HEADLIGHTS, SOS -- six
 * sensor types confirmed (2026-09-23, `GET /sensors/form?sensorType=...`)
 * to share one richer config shape: each `ios[]` entry carries its own
 * label plus *exactly* two raw-value-to-bilingual-label mappings (e.g. `0`
 * -> "Off"/"متوقف", `1` -> "On"/"يعمل"), enforced server-side via
 * `groups: [{ key: "ios[].values[]", minItems: 2, maxItems: 2 }]`. First
 * confirmed on PTO, then DOOR_STATUS; the user has confirmed the remaining
 * four share the identical field list/design -- see ValueMapConfig.
 */
export type ValueMapSensorType =
  | "DOOR_STATUS"
  | "PTO"
  | "SEAT_BELT"
  | "REFRIGERATOR_STATE"
  | "HEADLIGHTS"
  | "SOS";

export const VALUE_MAP_SENSOR_TYPES: readonly ValueMapSensorType[] = [
  "DOOR_STATUS",
  "PTO",
  "SEAT_BELT",
  "REFRIGERATOR_STATE",
  "HEADLIGHTS",
  "SOS",
];

export function isValueMapSensorType(
  sensorType: SensorType,
): sensorType is ValueMapSensorType {
  return (VALUE_MAP_SENSOR_TYPES as readonly SensorType[]).includes(sensorType);
}

/**
 * Only `WEIGHT` has a phase-1 built out; only build against this shape --
 * every other type's `config` stays untyped until its own step-2 form ships.
 */
export type WeightFormulaType = "NONE" | "SUM" | "PERCENT";

export interface WeightCalibrationPoint {
  readonly mv: number;
  readonly kg: number;
}

/**
 * One physical weight probe. `calibration: []` marks an ECU sensor (the
 * device reports kilograms directly) -- it can take part in a SUM or PERCENT
 * equation the same as a calibrated one.
 */
export interface WeightConfigIo {
  readonly io: number;
  /** Names one axle; most useful on SUM/PERCENT. */
  readonly label?: string;
  readonly minWeightKGs: number;
  readonly maxWeightKGs: number;
  readonly calibration: readonly WeightCalibrationPoint[];
  /** Set on every `ios[]` entry under PERCENT (summing to 100), `null` otherwise. */
  readonly ioFormulaPercentage: number | null;
}

/**
 * `formulaType` applies to the *whole* config -- every `ios[]` entry shares
 * it. NONE: one or more independent readings. SUM/PERCENT: two or more
 * entries combined into one equation (see WeightSensorConfigStep -- the
 * three step-2 "add" actions are mutually exclusive for exactly this
 * reason).
 */
export interface WeightConfig {
  readonly label: string;
  /** Defaults to "KGs". */
  readonly unitLabel: string;
  readonly formulaType: WeightFormulaType;
  readonly ios: readonly WeightConfigIo[];
}

export type TemperatureProbeType = "BLE" | "DALLAS";

/**
 * Despite the Figma field being labelled "Multiplier (0.1 Tzone BLE BT04, 1
 * Minew MST01)", the number doesn't scale anything -- it identifies the
 * beacon. Keep the field label as designed, but always send `beaconModel`;
 * a `multiplier` is server-derived on read and stripped (silently, not
 * rejected) if sent.
 */
export type TemperatureBeaconModel = "TZONE_BT04" | "MINEW_MST01";

export interface TemperatureChannel {
  readonly io: number;
  readonly unitLabel?: string;
}

/**
 * One physical probe. `probeType` selects the variant: BLE requires
 * `humidity` and `battery` and forbids `dallasId`; DALLAS forbids `battery`
 * (a wired probe draws power from the tracker, so it has no battery to
 * report) and allows `humidity`/`dallasId` as optional.
 */
export interface TemperatureConfigIo {
  readonly label: string;
  readonly probeType: TemperatureProbeType;
  readonly beaconModel: TemperatureBeaconModel;
  readonly temperature: TemperatureChannel;
  readonly humidity?: TemperatureChannel;
  readonly battery?: TemperatureChannel;
  readonly dallasId?: { readonly io: number };
}

/**
 * One entry per probe -- a trailer with a BLE beacon and a wired Dallas
 * probe is one configuration with two `ios[]` entries (see
 * TemperatureSensorConfigStep -- unlike WEIGHT, rows are fully independent;
 * there's no formula/equation coupling them).
 */
export interface TemperatureConfig {
  readonly label: string;
  readonly ios: readonly TemperatureConfigIo[];
}

/**
 * One physical IO reading, no other fields -- the whole config for any
 * {@link SimpleIoSensorType}. Matches the legacy admin screens for these
 * types exactly (a table of IO numbers, add/remove, nothing else in the
 * dialog).
 */
export interface SimpleIoConfigRow {
  readonly io: number;
}

export interface SimpleIoConfig {
  readonly label: string;
  readonly ios: readonly SimpleIoConfigRow[];
}

/** One RFID reader entry -- an IO number plus the tag's verification-mark length. */
export interface RfidConfigRow {
  readonly io: number;
  readonly tagLength: number;
}

export interface RfidConfig {
  readonly label: string;
  readonly ios: readonly RfidConfigRow[];
}

/**
 * One raw device value a {@link ValueMapSensorType} reports on an IO,
 * mapped to a human-readable bilingual label (e.g. `0` -> "Off"/"متوقف").
 * Every `ios[]` entry carries *exactly* two of these -- the server enforces
 * this via `groups: [{ key: "ios[].values[]", minItems: 2, maxItems: 2 }]`,
 * not modeled as its own field here since this app hardcodes the step-2
 * form rather than rendering fields generically (see ValueMapSensorConfigStep).
 */
export interface ValueMapConfigValue {
  readonly value: number;
  readonly labelEn: string;
  readonly labelAr: string;
}

/** One physical probe of a {@link ValueMapSensorType} -- unlike
 * SimpleIoConfig's rows, it carries its own label plus a two-way value
 * mapping per IO. */
export interface ValueMapConfigRow {
  readonly io: number;
  readonly label: string;
  /** Always exactly 2 entries. */
  readonly values: readonly ValueMapConfigValue[];
}

export interface ValueMapConfig {
  readonly label: string;
  readonly ios: readonly ValueMapConfigRow[];
}

export type SensorConfig =
  | WeightConfig
  | TemperatureConfig
  | SimpleIoConfig
  | RfidConfig
  | ValueMapConfig;

export type SensorAssignmentSortDirection = "asc" | "desc";
export type SensorAssignmentSortBy = "sensorType" | "createdAt";

/**
 * `GET /sensors` -- the Sensors Management list. Confirmed real
 * (2026-09-22). `search` matches sensor name, sensor type, company name (in
 * either language), or device IMEI/code.
 */
export interface SensorAssignmentsListQuery {
  readonly page?: number;
  readonly limit?: number;
  readonly sort?: SensorAssignmentSortDirection;
  readonly sortBy?: SensorAssignmentSortBy;
  readonly search?: string;
  readonly sensorType?: SensorType;
  /** World staff only; a client user is always scoped to their own account. */
  readonly accountId?: string;
  readonly deviceId?: string;
  /** Inclusive. */
  readonly createdFrom?: string;
  /** Inclusive. */
  readonly createdTo?: string;
}

/**
 * One row of `GET /sensors` -- a sensor (device + configuration pointer).
 * Confirmed real (2026-09-22): despite the swagger example showing `config`/
 * `updatedAt` on every row, the live endpoint's actual rows don't carry
 * them (validating as if they did broke the list -- see
 * sensorAssignmentListItemDtoSchema). Use `getById`/`SensorDetail` for the
 * full configuration. `configType`/`minValue`/`maxValue`/`unit`/`tagLength`
 * are `null` where the row's `sensorType` doesn't carry them -- render off
 * `sensorType`, not off which of these happen to be set.
 */
export interface SensorAssignmentListItem {
  readonly id: string;
  readonly sensorType: SensorType;
  /** `config.label`. */
  readonly name: string;
  /** WEIGHT only -- mirrors `WeightConfig.formulaType`. */
  readonly configType: WeightFormulaType | null;
  readonly minValue: number | null;
  readonly maxValue: number | null;
  readonly unit: string | null;
  /** RFID only. */
  readonly tagLength: number | null;
  readonly sensorConfig: {
    readonly id: string;
    /** How many devices share this configuration. */
    readonly deviceCount: number;
  };
  readonly device: {
    readonly id: string;
    readonly code: string;
    readonly imei: string;
  };
  readonly account: {
    readonly id: string;
    readonly nameEn: string;
    readonly nameAr: string;
  };
  readonly createdAt: string;
}

/**
 * `GET /sensors/:sensorId` and `GET /devices/:deviceId/sensors` -- both
 * confirmed real (2026-09-22): one sensor with its full configuration. Used
 * for the read-only detail screen and to populate the "copy existing
 * configuration" form once a source sensor is known.
 */
export interface SensorDetail extends SensorAssignmentListItem {
  readonly config: SensorConfig | null;
  readonly updatedAt: string;
}

export interface SensorFormOption {
  readonly value: string;
  readonly labelEn: string;
  readonly labelAr: string;
  readonly sortOrder: number;
}

export interface SensorFormFieldValidation {
  readonly min?: number;
  readonly max?: number;
  readonly minLength?: number;
  readonly maxLength?: number;
  readonly pattern?: string;
  readonly patternFlags?: string;
}

export type SensorFormFieldType =
  | "INT"
  | "FLOAT"
  | "DECIMAL"
  | "STRING"
  | "SELECT"
  | "BOOLEAN";

/**
 * One input's bounds, as `GET /sensors/form` returns it -- validation
 * bounds/options only, never layout (see SensorFormMeta).
 */
export interface SensorFormField {
  /** A dotted path into `config`, `[]` marking a repeatable group (e.g. `ios[].calibration[].mv`). */
  readonly key: string;
  readonly type: SensorFormFieldType;
  readonly labelEn: string;
  readonly labelAr: string;
  readonly requiredIn: readonly string[];
  readonly forbiddenIn: readonly string[];
  readonly optionGroup?: string;
  readonly validation: SensorFormFieldValidation;
  readonly sortOrder: number;
}

/**
 * `GET /sensors/form?sensorType=` -- the option lists and validation bounds
 * the server enforces for one sensor type, cached per `sensorType` for the
 * session. `variantKey`/`variants` name the field that branches the shape
 * (`formulaType` for WEIGHT); types with no branch report a single
 * `"DEFAULT"` variant.
 */
export interface SensorFormMeta {
  readonly sensorType: SensorType;
  readonly variantKey: string | null;
  readonly variants: readonly string[];
  readonly options: Readonly<Record<string, readonly SensorFormOption[]>>;
  readonly fields: readonly SensorFormField[];
  readonly rules: readonly string[];
}

/**
 * PLANNED backend extension (confirmed still absent as of the 2026-09-22
 * contract): `POST /sensors` takes a literal `deviceIds: string[]` and
 * nothing else -- no "all"/branch concept exists on the wire today. The
 * target section's "all devices" and "branch" modes need the server to
 * resolve which devices that means (a company can hold far more devices
 * than a client should page through and enumerate); backend has said this
 * is landing as an extension to the same endpoint. Until it ships,
 * `ALL`/`BRANCH` scopes only work against the mock repository --
 * {@link createSensorAssignmentsHttpRepository}'s `create` throws if asked
 * to send one, rather than guess at a wire shape for it. `DEVICES` is the
 * one shape the real contract already supports, flattened straight to
 * `deviceIds` on the wire (see sensorCreateInputToDto).
 */
export type SensorTargetScope =
  | { readonly type: "ALL"; readonly accountId: string }
  | { readonly type: "BRANCH"; readonly branchId: string }
  | { readonly type: "DEVICES"; readonly deviceIds: readonly string[] };

/**
 * `POST /sensors` -- either a brand-new configuration (`sensorType` +
 * `config`, the two kept correlated so e.g. `sensorType: "WEIGHT"` can't be
 * paired with a `TemperatureConfig`) or a reference to an existing one to
 * repoint the target at (`sensorConfigId`). Never both, never neither. See
 * {@link SensorTargetScope} for why `target` isn't just `deviceIds` yet.
 */
export type SensorCreateInput =
  | {
      readonly target: SensorTargetScope;
      readonly sensorType: "WEIGHT";
      readonly config: WeightConfig;
    }
  | {
      readonly target: SensorTargetScope;
      readonly sensorType: "TEMPERATURE";
      readonly config: TemperatureConfig;
    }
  | {
      readonly target: SensorTargetScope;
      readonly sensorType: SimpleIoSensorType;
      readonly config: SimpleIoConfig;
    }
  | {
      readonly target: SensorTargetScope;
      readonly sensorType: "RFID";
      readonly config: RfidConfig;
    }
  | {
      readonly target: SensorTargetScope;
      readonly sensorType: ValueMapSensorType;
      readonly config: ValueMapConfig;
    }
  | {
      readonly target: SensorTargetScope;
      readonly sensorConfigId: string;
    };

export interface SensorCreateResultData {
  readonly sensorConfigId: string;
  /** Sensors newly assigned -- full rows, same shape `getById` returns. */
  readonly created: readonly SensorDetail[];
  /** Sensors that already existed on a named device and were repointed. */
  readonly updated: readonly SensorDetail[];
}

/** One device `POST /sensors` refused for an IO clash -- see SensorCreateResult. */
export interface SensorIoConflictEntry {
  readonly deviceId: string;
  readonly imei: string;
  readonly io: number;
}

/**
 * `POST /sensors` answers 201 on success, or 409 (code
 * `SENSOR_IO_ALREADY_ASSIGNED`) when a named device already reads one of the
 * submitted IOs on a *different* sensor -- the whole write is all-or-nothing,
 * so the repository catches that specific 409 and folds it back into this
 * result type instead of throwing, the same way `CreateDeviceResult` folds
 * in `ARCHIVED_DEVICE_FOUND`.
 */
export type SensorCreateResult =
  | ({ readonly status: "created" } & SensorCreateResultData)
  | {
      readonly status: "io-conflict";
      readonly message: string;
      readonly conflicts: readonly SensorIoConflictEntry[];
    };

/**
 * `PUT /sensors/:sensorId` -- replaces one sensor's configuration wholesale
 * (send the complete `config`, never a partial merge). `sensorType` and the
 * device can't change; moving what a sensor reads, or which device it's on,
 * is a delete plus a create instead. Confirmed real (2026-09-22) but not
 * wired to any screen yet -- the list's row actions are still stubbed.
 */
export type SensorUpdateInput =
  | { readonly sensorType: "WEIGHT"; readonly config: WeightConfig }
  | { readonly sensorType: "TEMPERATURE"; readonly config: TemperatureConfig }
  | { readonly sensorType: SimpleIoSensorType; readonly config: SimpleIoConfig }
  | { readonly sensorType: "RFID"; readonly config: RfidConfig }
  | { readonly sensorType: ValueMapSensorType; readonly config: ValueMapConfig };

/** `PUT /sensors/:sensorId` answers the same 409 shape `POST /sensors` does. */
export type SensorUpdateResult =
  | { readonly status: "updated"; readonly sensor: SensorDetail }
  | {
      readonly status: "io-conflict";
      readonly message: string;
      readonly conflicts: readonly SensorIoConflictEntry[];
    };

/**
 * `DELETE /sensors/:sensorId` -- confirmed real (2026-09-22). Detaches the
 * sensor from its device; if it was the last device on that configuration,
 * the configuration is retired too (invisibly). Soft delete server-side
 * (archived, for the audit trail), but there's no restore on this app --
 * unlike devices/assets, a sensor configuration is cheap to recreate, so no
 * archived-sensors screen exists and this reads as permanent from the UI.
 */
export interface SensorDeleteResult {
  readonly responseMessage: string;
}
